import { useEffect } from "react";
import { useEditorStore } from "../app/store";
import { listStoredPatches, loadActivePatch, persistDraft, persistPatch } from "./database";
import { enqueuePersistence } from "./write-queue";

let hydrationPromise: Promise<{
  document: Awaited<ReturnType<typeof loadActivePatch>>["document"];
  recoveredDraft: boolean;
  library: Awaited<ReturnType<typeof listStoredPatches>>;
}> | null = null;

export function usePatchPersistence() {
  const hydrationState = useEditorStore((state) => state.hydrationState);
  const hydrated = hydrationState === "ready";
  const document = useEditorStore((state) => state.activeDocument);
  const documentRevision = useEditorStore((state) => state.documentRevision);
  const saveUrgency = useEditorStore((state) => state.saveUrgency);
  const libraryOperation = useEditorStore((state) => state.libraryOperation);
  const beginHydration = useEditorStore((state) => state.beginHydration);
  const hydrateDocument = useEditorStore((state) => state.hydrateDocument);
  const failHydration = useEditorStore((state) => state.failHydration);
  const markSaving = useEditorStore((state) => state.markSaving);
  const markSaved = useEditorStore((state) => state.markSaved);
  const markSaveError = useEditorStore((state) => state.markSaveError);

  useEffect(() => {
    if (hydrationState === "idle") beginHydration();
    if (hydrationState !== "idle" && hydrationState !== "loading") return;

    hydrationPromise ??= Promise.all([loadActivePatch(), listStoredPatches()]).then(
      ([active, library]) => ({ ...active, library }),
    );
    hydrationPromise
      .then(({ document: loadedDocument, recoveredDraft, library }) => {
        hydrateDocument(loadedDocument, recoveredDraft, library);
      })
      .catch((error: unknown) => {
        failHydration(error instanceof Error ? error.message : String(error));
      });
  }, [beginHydration, failHydration, hydrateDocument, hydrationState]);

  useEffect(() => {
    if (!hydrated || libraryOperation) return;
    const revision = documentRevision;
    let cancelled = false;

    enqueuePersistence(() => persistDraft(document)).catch((error: unknown) => {
      if (!cancelled) markSaveError(error instanceof Error ? error.message : String(error));
    });

    const timeout = window.setTimeout(
      () => {
        markSaving(revision);
        enqueuePersistence(() => persistPatch(document))
          .then(() => {
            if (!cancelled) markSaved(revision, new Date().toISOString());
          })
          .catch((error: unknown) => {
            if (!cancelled) markSaveError(error instanceof Error ? error.message : String(error));
          });
      },
      saveUrgency === "immediate" ? 0 : 650,
    );

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [
    document,
    documentRevision,
    hydrated,
    libraryOperation,
    markSaveError,
    markSaved,
    markSaving,
    saveUrgency,
  ]);
}
